import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Business,
  TrendingUp,
  Assessment,
  Speed,
  CheckCircle,
  ExpandMore,
  Psychology,
  Analytics,
  Security,
  CloudQueue,
  Code,
  Group
} from '@mui/icons-material';

const Consulting = () => {
  const benefits = [
    'Drive innovation and simplify digital transformation with strategic visioning',
    'Combine deep industry expertise with modern skills and advanced technology',
    'Help overcome business challenges and uncover new opportunities for growth',
    'Provide outcome-driven technology consulting and implementation',
    'Enable faster time-to-value with flexible and scaled solutions',
    'Access global network of technology strategy and implementation consultants'
  ];

  const services = [
    {
      title: 'Digital Transformation Strategy',
      description: 'Comprehensive digital transformation planning and execution',
      features: ['Strategy Development', 'Technology Roadmap', 'Change Management', 'Implementation Planning'],
      benefits: ['Clear Vision', 'Reduced Risk', 'Faster Implementation', 'Better ROI']
    },
    {
      title: 'Technology Architecture',
      description: 'Design and implement modern technology architectures',
      features: ['Architecture Design', 'Technology Selection', 'Integration Planning', 'Performance Optimization'],
      benefits: ['Scalable Solutions', 'Improved Performance', 'Cost Optimization', 'Future-Proof Design']
    },
    {
      title: 'Organizational Change Management',
      description: 'Guide organizations through technology-driven change',
      features: ['Change Strategy', 'Training Programs', 'Communication Planning', 'Adoption Support'],
      benefits: ['Smooth Transitions', 'Higher Adoption', 'Reduced Resistance', 'Better Outcomes']
    },
    {
      title: 'Technology Assessment',
      description: 'Evaluate and optimize existing technology investments',
      features: ['Current State Analysis', 'Gap Assessment', 'Recommendations', 'Implementation Support'],
      benefits: ['Cost Reduction', 'Performance Improvement', 'Risk Mitigation', 'Strategic Alignment']
    }
  ];

  const customerStories = [
    {
      title: 'Global Manufacturing Company',
      description: 'Digital transformation strategy for 50,000+ employee organization',
      outcome: 'Developed comprehensive digital transformation roadmap resulting in 40% improvement in operational efficiency and $50M in cost savings over 3 years.',
      industry: 'Manufacturing',
      services: ['Digital Strategy', 'Technology Architecture']
    },
    {
      title: 'Financial Services Firm',
      description: 'Technology modernization and cloud migration strategy',
      outcome: 'Successfully migrated legacy systems to cloud, reducing infrastructure costs by 60% while improving security and compliance posture.',
      industry: 'Financial Services',
      services: ['Cloud Strategy', 'Security Consulting']
    }
  ];

  const faqs = [
    {
      question: 'What is technology consulting?',
      answer: 'Technology consulting involves providing expert guidance on technology strategy, implementation, and optimization to help organizations achieve their business objectives. Our consultants combine deep technical expertise with business acumen to deliver solutions that drive real business value.'
    },
    {
      question: 'Why choose Kyndryl for consulting services?',
      answer: 'We have the operational knowledge, experts and alliances to help redefine the enterprise business relationship with IT. Our global network of technology strategy and implementation consultants, architects and engineers have deep domain knowledge based on decades of experience with some of the world\'s most complex technology environments.'
    },
    {
      question: 'How would my business benefit from consulting engagement?',
      answer: 'We are the largest global infrastructure consulting, implementation and managed services provider with 30+ years\' experience building and running some of the most complex technology environments in the world. We work with your vendor ecosystem through flexible and scaled solutions so your business can quickly reach time to value.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Technology Consulting Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Drive innovation and simplify digital transformation with strategic visioning, architecture design, and organizational change management
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* What We Do Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Transform Your Business with Expert Guidance
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 8,
              lineHeight: 1.6
            }}
          >
            Our services combine deep industry expertise with modern skills and advanced technology to help you 
            overcome business challenges and uncover new opportunities for growth. We provide outcome-driven 
            technology consulting as your ideal transformation partner.
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Consulting Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Key Features:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Benefits:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.benefits.map((benefit, idx) => (
                          <Chip
                            key={idx}
                            label={benefit}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#7b1fa2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Stories Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Customer Success Stories
          </Typography>

          <Grid container spacing={4}>
            {customerStories.map((story, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {story.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      {story.description}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {story.outcome}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={story.industry}
                        sx={{
                          bgcolor: '#e8f5e8',
                          color: '#2e7d32',
                          fontWeight: 'bold'
                        }}
                      />
                      <Stack direction="row" spacing={1}>
                        {story.services.map((service, idx) => (
                          <Chip
                            key={idx}
                            label={service}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Start Your Transformation Journey
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Get expert guidance on your digital transformation and technology strategy
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request a Consultation
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Consulting;
